import {
  PageLayout,
  Section,
} from "@/components/layout";
import { Button } from "@/components/ui/button";
import { useClientTranslation } from "@/hooks/use-client-translation";
import { Link } from "react-router-dom";

/* -------------------------------------------------------------------------- */
/*                              Hero Section                                  */
/* -------------------------------------------------------------------------- */

function HeroSection() {
  const { t } = useClientTranslation();

  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    window.open("mailto:info@mezepay.com", "_self");
  };

  return (
    <section className="w-full bg-white lg:h-[calc(100vh-var(--navbar-height-desktop))]">
      <div className="flex flex-col lg:flex-row items-stretch h-full">
        {/* Left Column - Content */}
        <div className="flex flex-col justify-center items-start px-5 py-16 gap-6 lg:flex-1 lg:pl-px-desktop lg:pr-20 lg:py-0 lg:gap-8">
          <div className="max-w-[560px] flex flex-col gap-6 lg:gap-8">
            {/* Content */}
            <div className="flex flex-col gap-5 lg:gap-6">
              <h1 className="max-w-[70%] lg:max-w-none text-4xl md:text-5xl lg:text-[72px] font-medium leading-[1.2] tracking-tighter text-black">
                {t("hero.title")}
              </h1>
              <p className="text-base lg:text-lg font-normal leading-[1.5] text-black">
                {t("hero.subtitle")}
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-row gap-4">
              <Button
                className="bg-gradient-to-r from-purple-primary to-yellow-accent text-white rounded-full px-6 py-2.5 text-base font-medium h-11"
                onClick={handleContactClick}
              >
                {t("hero.contact")}
              </Button>
              <Button
                variant="outline"
                className="rounded-full px-6 py-2.5 text-base font-medium border-black/15 text-black h-11"
                onClick={() => {
                  const footer =
                    document.getElementById(
                      "contact",
                    );
                  const emailInput =
                    document.getElementById(
                      "footer-email-input",
                    );

                  footer?.scrollIntoView({
                    behavior: "smooth",
                  });

                  // Focus the email input after scrolling
                  setTimeout(() => {
                    emailInput?.focus();
                  }, 500);
                }}
              >
                {t("hero.demo")}
              </Button>
            </div>
          </div>
        </div>

        {/* Right Column - Image */}
        <div
          className="h-[348px] lg:h-auto lg:flex-1 bg-gray-200 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              "url('/header.jpg')",
          }}
        />
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*                          Timeline / How It Works                           */
/* -------------------------------------------------------------------------- */

interface TimelineStep {
  title: string;
  description: string;
  image: string;
}

function TimelineSection() {
  const { t } = useClientTranslation();

  const steps: TimelineStep[] = [
    {
      title: t("howItWorks.steps.scan.title"),
      description: t(
        "howItWorks.steps.scan.description",
      ),
      image: "/images/scan-qr.svg",
    },
    {
      title: t("howItWorks.steps.split.title"),
      description: t(
        "howItWorks.steps.split.description",
      ),
      image: "/images/view-menu.svg",
    },
    {
      title: t("howItWorks.steps.pay.title"),
      description: t(
        "howItWorks.steps.pay.description",
      ),
      image: "/images/pay-bill.svg",
    },
    {
      title: t("howItWorks.steps.tip.title"),

      description: t(
        "howItWorks.steps.tip.description",
      ),
      image: "/images/add-tip.svg",
    },
    {
      title: t("howItWorks.steps.review.title"),
      description: t(
        "howItWorks.steps.review.description"
      ),
      image: "/images/leave-review.svg",
    },
  ];

  return (
    <Section
      id="how-it-works"
      className="bg-white pt-12 pb-4 px-5 lg:py-16 lg:px-px-desktop"
    >
      <div className="flex flex-col items-center gap-10 lg:gap-14 max-w-container mx-auto">
        {/* Section Header */}
        <div className="flex flex-col items-center gap-3 lg:gap-4 max-w-[768px] text-center">
          <span className="tagline">
            {t("howItWorks.title")}
          </span>
          <h2 className="text-4xl lg:text-5xl font-medium leading-[1.2] tracking-[-0.01em] text-black">
            {t("howItWorks.subtitle")}
          </h2>
        </div>

        {/* Timeline Progress - Mobile: vertical column, Desktop: horizontal row */}
        <div className="w-full flex flex-col lg:flex-row lg:items-stretch">
          {steps.map((step, index) => {
            const isLast =
              index === steps.length - 1;

            return (
              <div
                key={index}
                className="flex-1 flex flex-row lg:flex-col items-start lg:items-center gap-4"
              >
                {/* Image */}
                <div
                  className="w-[98px] h-[98px] lg:w-[204px] lg:h-[204px] shrink-0 bg-gray-200 rounded-lg bg-cover bg-center"
                  style={{
                    backgroundImage: `url('${step.image}')`,
                  }}
                />

                {/* Progress indicator - Mobile: vertical line with circle at top, Desktop: horizontal */}
                <div className="relative flex flex-col lg:flex-row items-center pt-2 lg:pt-4 gap-2 lg:gap-0 w-[15px] lg:w-full min-h-[180px] lg:min-h-0">
                  {/* Circle - at top on mobile */}
                  <div className="w-[15px] h-[15px] rounded-full bg-black shrink-0 lg:hidden" />

                  {/* Vertical line extending down from circle (mobile only) */}
                  {!isLast && (
                    <div className="w-[3px] flex-1 bg-black lg:hidden" />
                  )}

                  {/* Desktop horizontal lines */}
                  <div className="hidden lg:block lg:flex-1 lg:h-[3px] lg:bg-black" />
                  <div className="hidden lg:block w-[15px] h-[15px] rounded-full bg-black shrink-0" />
                  <div className="hidden lg:block lg:flex-1 lg:h-[3px] lg:bg-black" />
                </div>

                {/* Content */}
                <div className="flex flex-col justify-start items-start lg:items-center px-2 pb-4 lg:px-0 lg:pb-0 gap-2 flex-1">
                  <h5 className="text-xl font-medium leading-[1.4] tracking-[-0.01em] text-black lg:text-center self-stretch">
                    {step.title}
                  </h5>
                  <p className="text-base font-normal leading-[1.5] text-black lg:text-center self-stretch">
                    {step.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/*                        Layout Section (For Restaurants)                    */
/* -------------------------------------------------------------------------- */

function ForRestaurantsSection() {
  const { t } = useClientTranslation();

  return (
    <Section
      id="for-restaurants"
      className="bg-white py-10 px-5 lg:py-16 lg:px-px-desktop"
    >
      <div className="flex flex-col lg:flex-row items-start lg:items-center gap-10 lg:gap-14 max-w-container mx-auto">
        {/* Content - Order 0 on mobile, Order 1 on desktop */}
        <div className="flex-1 flex flex-col gap-6 lg:gap-8 order-first lg:order-last">
          {/* Section Title */}
          <div className="flex flex-col gap-3 lg:gap-4">
            <span className="text-base font-semibold leading-[1.5] text-black">
              {t("forRestaurants.label")}
            </span>
            <div className="flex flex-col gap-5 lg:gap-6">
              <h2 className="text-4xl lg:text-[52px] font-medium leading-[1.2] lg:!leading-[3.5rem] tracking-[-0.01em] text-black">
                {t("solutions.title")}
              </h2>
              <p className="text-base lg:text-lg font-normal leading-[1.5] text-black">
                {t("solutions.subtitle")}
              </p>
            </div>
          </div>

          {/* Actions */}
          <Button
            variant="outline"
            className="w-fit rounded-full px-6 py-2.5 text-base font-medium border-black/15 text-black h-11"
            asChild
          >
            <Link to="/for-restaurants">
              {t("forRestaurants.learnMore")}
            </Link>
          </Button>
        </div>

        {/* Image - Order 1 on mobile, Order 0 on desktop */}
        <div
          className="flex-1 w-full h-[348px] lg:h-[640px] bg-gray-200 rounded-lg bg-cover bg-center order-last lg:order-first"
          style={{
            backgroundImage:
              "url('/images/bottom-hero-home.svg')",
          }}
        />
      </div>
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/*                               Home Page                                    */
/* -------------------------------------------------------------------------- */

export default function HomePage() {
  return (
    <PageLayout activePage="home">
      <main className="flex-1">
        <HeroSection />
        <TimelineSection />
        <ForRestaurantsSection />
      </main>
    </PageLayout>
  );
}
